/**
 * Signaling Service
 *
 * Encapsulates all Socket.io signaling logic for WebRTC.
 * Single source of truth for socket events - no more scattered emit/on calls!
 */

import { io, Socket } from 'socket.io-client';
import { CallStateMachine } from '@/lib/webrtc/state-machine';

/**
 * Socket event payloads
 */
interface OfferPayload {
  roomId: string;
  offer: RTCSessionDescriptionInit;
}

interface AnswerPayload {
  roomId: string;
  answer: RTCSessionDescriptionInit;
}

interface IceCandidatePayload {
  roomId: string;
  candidate: RTCIceCandidateInit;
}

interface VideoStatePayload {
  roomId: string;
  enabled?: boolean;
  isVideoOn?: boolean;
}

interface RecordingStatePayload {
  roomId: string;
  isRecording: boolean;
}

/**
 * Event handler types
 */
type EventHandler<T = void> = (data: T) => void;
type UnsubscribeFn = () => void;

/**
 * SignalingService configuration
 */
export interface SignalingConfig {
  url: string;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  timeout?: number;
}

/**
 * Signaling Service Class
 */
export class SignalingService {
  private socket: Socket | null = null;
  private stateMachine: CallStateMachine | null = null;
  private roomId: string | null = null;
  private isHost: boolean = false;
  private pendingListeners: Array<{ event: string; listener: Function }> = [];
  private callEndedByHost: boolean = false;

  constructor(private config: SignalingConfig) {}

  /**
   * Sets the state machine for automatic state updates
   */
  setStateMachine(stateMachine: CallStateMachine): void {
    this.stateMachine = stateMachine;
  }

  /**
   * Connects to the signaling server
   */
  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.socket = io(this.config.url, {
        transports: ['websocket'],
        reconnectionAttempts: this.config.reconnectionAttempts ?? 10,
        reconnectionDelay: this.config.reconnectionDelay ?? 1000,
        timeout: this.config.timeout ?? 10000,
      });

      this.socket.on('connect', () => {
        console.log('[SignalingService] Connected to signaling server');

        // ✅ Attach all pending listeners that were registered before connection
        console.log(`[SignalingService] Attaching ${this.pendingListeners.length} pending listeners`);
        this.pendingListeners.forEach(({ event, listener }) => {
          this.socket?.on(event, listener as any);
        });
        this.pendingListeners = []; // Clear after attaching

        resolve();
      });

      this.socket.on('connect_error', (error) => {
        console.error('[SignalingService] Connection error:', error);
        reject(error);
      });

      this.socket.on('disconnect', (reason) => {
        console.log('[SignalingService] Disconnected:', reason);
      });

      // Handle socket reconnection
      this.socket.on('reconnect', (attemptNumber) => {
        console.log(`[SignalingService] Reconnected after ${attemptNumber} attempts`);

        // Auto-rejoin room if we were in one before disconnecting
        if (this.roomId && this.isHost !== null) {
          console.log(`[SignalingService] Auto-rejoining room ${this.roomId} as ${this.isHost ? 'host' : 'guest'}`);
          this.joinRoom(this.roomId, this.isHost).catch((err) => {
            console.error('[SignalingService] Failed to auto-rejoin room after reconnection:', err);
          });
        }
      });

      this.socket.on('reconnect_attempt', (attemptNumber) => {
        console.log(`[SignalingService] Reconnection attempt ${attemptNumber}`);
      });

      this.socket.on('reconnect_error', (error) => {
        console.error('[SignalingService] Reconnection error:', error);
      });

      this.socket.on('reconnect_failed', () => {
        console.error('[SignalingService] Reconnection failed - all attempts exhausted');
      });
    });
  }

  /**
   * Joins a room
   */
  async joinRoom(roomId: string, isHost: boolean): Promise<void> {
    if (!this.socket) {
      throw new Error('Socket not connected. Call connect() first.');
    }

    this.roomId = roomId;
    this.isHost = isHost;

    return new Promise((resolve, reject) => {
      // ✅ FIX: Backend expects { roomId, userId, userName, role }
      const payload = {
        roomId,
        userId: this.socket!.id,
        userName: isHost ? 'Host' : 'Guest',
        role: (isHost ? 'agent' : 'guest') as 'agent' | 'guest',
      };

      // ✅ NEW: Handle callback to check if join was successful
      this.socket!.emit('join-room', payload, (response: { success: boolean; error?: string; expired?: boolean }) => {
        console.log(`[SignalingService] Join room response:`, response);

        if (!response.success) {
          const errorMessage = response.error || 'Failed to join call';
          console.error(`[SignalingService] Failed to join room: ${errorMessage}`);
          reject(new Error(errorMessage));
          return;
        }

        console.log(`[SignalingService] Successfully joined room: ${roomId} as ${isHost ? 'host' : 'guest'}`);
        resolve();
      });
    });
  }

  /**
   * Leaves the current room
   */
  async leaveRoom(): Promise<void> {
    if (!this.socket || !this.roomId) {
      return;
    }

    return new Promise((resolve) => {
      // ✅ FIX: Backend expects { roomId }
      this.socket!.emit('leave-room', { roomId: this.roomId });
      console.log('[SignalingService] Left room:', this.roomId);
      this.roomId = null;
      resolve();
    });
  }

  /**
   * Sends an offer to the peer
   */
  sendOffer(offer: RTCSessionDescriptionInit): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to a room');
    }

    // ✅ FIX: Add 'from' field required by backend
    const payload = {
      roomId: this.roomId,
      offer,
      from: this.socket.id,
    };

    this.socket.emit('offer', payload);
    console.log('[SignalingService] Sent offer');

    this.stateMachine?.transition({ type: 'OFFER_SENT' });
  }

  /**
   * Sends an answer to the peer
   */
  sendAnswer(answer: RTCSessionDescriptionInit): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to a room');
    }

    // ✅ FIX: Add 'from' field required by backend
    const payload = {
      roomId: this.roomId,
      answer,
      from: this.socket.id,
    };

    this.socket.emit('answer', payload);
    console.log('[SignalingService] Sent answer');

    this.stateMachine?.transition({ type: 'ANSWER_SENT' });
  }

  /**
   * Sends an ICE candidate to the peer
   */
  sendIceCandidate(candidate: RTCIceCandidateInit): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to a room');
    }

    // ✅ FIX: Add 'from' field required by backend
    const payload = {
      roomId: this.roomId,
      candidate,
      from: this.socket.id,
    };

    this.socket.emit('ice-candidate', payload);
  }

  /**
   * Notifies peer that stream is ready (guest joins first scenario)
   */
  notifyStreamReady(): void {
    if (!this.socket || !this.roomId) {
      return;
    }

    this.socket.emit('guest-stream-ready', { roomId: this.roomId });
    console.log('[SignalingService] Notified stream ready');
  }

  /**
   * Notifies peer of video state change (on/off)
   */
  notifyVideoState(enabled: boolean): void {
    if (!this.socket || !this.roomId) {
      return;
    }

    const payload: VideoStatePayload = {
      roomId: this.roomId,
      enabled,
    };

    this.socket.emit('video-state', payload);
  }

  /**
   * Notifies peer of recording state change
   */
  notifyRecordingState(isRecording: boolean): void {
    if (!this.socket || !this.roomId) {
      return;
    }

    const payload: RecordingStatePayload = {
      roomId: this.roomId,
      isRecording,
    };

    this.socket.emit('recording-state', payload);
  }

  /**
   * Ends the call (host only)
   */
  endCall(): void {
    if (!this.socket || !this.roomId) {
      return;
    }

    // ✅ FIX: Backend expects { roomId }
    this.socket.emit('end-call', { roomId: this.roomId });
    console.log('[SignalingService] Ended call');
  }

  // ========================================
  // Event Listeners
  // ========================================

  /**
   * Listens for peer joined event
   */
  onPeerJoined(handler: EventHandler<string>): UnsubscribeFn {
    const listener = (data: { userId: string; userName: string; role: string }) => {
      console.log('\n' + '🎧'.repeat(30));
      console.log('[SignalingService] 📥 RECEIVED user-joined event from backend');
      console.log('[SignalingService] Peer data:', data);
      console.log('[SignalingService] Current socket ID:', this.socket?.id);
      console.log('[SignalingService] Calling handler with userId:', data.userId);
      console.log('🎧'.repeat(30) + '\n');

      // Note: State transition removed - let useWebRTC hook manage state
      handler(data.userId);
    };

    // If socket is already connected, attach immediately
    // ✅ FIX: Backend emits 'user-joined', not 'peer-joined'
    if (this.socket) {
      console.log('[SignalingService] 🎧 Registering user-joined listener (socket connected)');
      this.socket.on('user-joined', listener);
    } else {
      console.log('[SignalingService] 🎧 Queueing user-joined listener (socket not connected yet)');
      // Queue for later attachment when socket connects
      this.pendingListeners.push({ event: 'user-joined', listener });
    }

    return () => this.socket?.off('user-joined', listener);
  }

  /**
   * Listens for offer received event
   */
  onOffer(handler: EventHandler<RTCSessionDescriptionInit>): UnsubscribeFn {
    const listener = (data: OfferPayload) => {
      console.log('[SignalingService] Received offer');
      this.stateMachine?.transition({
        type: 'OFFER_RECEIVED',
        offer: new RTCSessionDescription(data.offer),
      });
      handler(data.offer);
    };

    if (this.socket) {
      this.socket.on('offer', listener);
    } else {
      this.pendingListeners.push({ event: 'offer', listener });
    }

    return () => this.socket?.off('offer', listener);
  }

  /**
   * Listens for answer received event
   */
  onAnswer(handler: EventHandler<RTCSessionDescriptionInit>): UnsubscribeFn {
    const listener = (data: AnswerPayload) => {
      console.log('[SignalingService] Received answer');
      this.stateMachine?.transition({
        type: 'ANSWER_RECEIVED',
        answer: new RTCSessionDescription(data.answer),
      });
      handler(data.answer);
    };

    if (this.socket) {
      this.socket.on('answer', listener);
    } else {
      this.pendingListeners.push({ event: 'answer', listener });
    }

    return () => this.socket?.off('answer', listener);
  }

  /**
   * Listens for ICE candidate received event
   */
  onIceCandidate(handler: EventHandler<RTCIceCandidateInit>): UnsubscribeFn {
    const listener = (data: IceCandidatePayload) => {
      handler(data.candidate);
    };

    if (this.socket) {
      this.socket.on('ice-candidate', listener);
    } else {
      this.pendingListeners.push({ event: 'ice-candidate', listener });
    }

    return () => this.socket?.off('ice-candidate', listener);
  }

  /**
   * Listens for peer left event
   */
  onPeerLeft(handler: (data: { socketId: string; isHost: boolean; canRejoin?: boolean } | string) => void): UnsubscribeFn {
    const listener = (data: any) => {
      // ✅ FIX: Backend emits 'user-left' with { userId, canRejoin? }
      // Convert to expected format
      const peerData = data.userId
        ? { socketId: data.userId, isHost: false, canRejoin: data.canRejoin }
        : typeof data === 'string'
        ? { socketId: data, isHost: false }
        : data;

      if (data.canRejoin) {
        console.log('[SignalingService] Peer left (can rejoin within 30 minutes):', peerData);
      } else {
        console.log('[SignalingService] Peer left:', peerData);
      }

      this.stateMachine?.transition({ type: 'PEER_LEFT' });
      handler(peerData);
    };

    // ✅ FIX: Backend emits 'user-left', not 'peer-left'
    if (this.socket) {
      this.socket.on('user-left', listener);
    } else {
      this.pendingListeners.push({ event: 'user-left', listener });
    }

    return () => this.socket?.off('user-left', listener);
  }

  /**
   * Listens for call ended by host event
   */
  onCallEndedByHost(handler: EventHandler): UnsubscribeFn {
    const listener = () => {
      console.log('[SignalingService] Call ended');
      // ✅ Set flag to prevent duplicate "peer left" toast
      this.callEndedByHost = true;
      this.stateMachine?.transition({ type: 'HOST_LEFT' });
      handler();
    };

    // ✅ FIX: Backend emits 'call-ended', not 'call-ended-by-host'
    if (this.socket) {
      this.socket.on('call-ended', listener);
    } else {
      this.pendingListeners.push({ event: 'call-ended', listener });
    }

    return () => this.socket?.off('call-ended', listener);
  }

  /**
   * Checks if call was ended by host (to prevent duplicate toasts)
   */
  wasCallEndedByHost(): boolean {
    return this.callEndedByHost;
  }

  /**
   * Listens for guest stream ready event (host side)
   */
  onGuestStreamReady(handler: EventHandler): UnsubscribeFn {
    const listener = () => {
      console.log('[SignalingService] Guest stream ready');
      handler();
    };

    if (this.socket) {
      this.socket.on('guest-stream-ready', listener);
    } else {
      this.pendingListeners.push({ event: 'guest-stream-ready', listener });
    }

    return () => this.socket?.off('guest-stream-ready', listener);
  }

  /**
   * Listens for video state change event
   */
  onVideoState(handler: EventHandler<boolean>): UnsubscribeFn {
    const listener = (data: VideoStatePayload) => {
      const enabled = typeof data.enabled === 'boolean' ? data.enabled : !!data.isVideoOn;
      console.log('[SignalingService] Peer video state:', enabled);
      handler(enabled);
    };

    if (this.socket) {
      this.socket.on('video-state', listener);
    } else {
      this.pendingListeners.push({ event: 'video-state', listener });
    }

    return () => this.socket?.off('video-state', listener);
  }

  /**
   * Listens for recording state change event
   */
  onRecordingState(handler: EventHandler<boolean>): UnsubscribeFn {
    const listener = (data: RecordingStatePayload) => {
      console.log('[SignalingService] Peer recording state:', data.isRecording);
      handler(data.isRecording);
    };

    if (this.socket) {
      this.socket.on('recording-state', listener);
    } else {
      this.pendingListeners.push({ event: 'recording-state', listener });
    }

    return () => this.socket?.off('recording-state', listener);
  }

  // ========================================
  // Perfect Negotiation Events
  // ========================================

  /**
   * Listen for offer collision events from backend
   */
  onOfferCollision(handler: EventHandler<{ shouldRollback: boolean }>): UnsubscribeFn {
    const listener = (data: { shouldRollback: boolean }) => {
      console.log('[SignalingService] Offer collision detected:', data);
      handler(data);
    };

    if (this.socket) {
      this.socket.on('offer-collision', listener);
    } else {
      this.pendingListeners.push({ event: 'offer-collision', listener });
    }

    return () => this.socket?.off('offer-collision', listener);
  }

  /**
   * Listen for offer acknowledgment from backend
   */
  onOfferAck(handler: EventHandler<{ timestamp: number }>): UnsubscribeFn {
    const listener = (data: { timestamp: number }) => {
      console.log('[SignalingService] Offer acknowledged at:', data.timestamp);
      handler(data);
    };

    if (this.socket) {
      this.socket.on('offer-ack', listener);
    } else {
      this.pendingListeners.push({ event: 'offer-ack', listener });
    }

    return () => this.socket?.off('offer-ack', listener);
  }

  /**
   * Listen for batched ICE candidates (sent after SDP exchange)
   */
  onICECandidatesBatch(handler: EventHandler<RTCIceCandidateInit[]>): UnsubscribeFn {
    const listener = (data: { candidates: RTCIceCandidateInit[] }) => {
      console.log('[SignalingService] Received ICE batch:', data.candidates.length);
      handler(data.candidates);
    };

    if (this.socket) {
      this.socket.on('ice-candidates-batch', listener);
    } else {
      this.pendingListeners.push({ event: 'ice-candidates-batch', listener });
    }

    return () => this.socket?.off('ice-candidates-batch', listener);
  }

  /**
   * Listens for annotation sync events
   */
  onAnnotationSync(handler: EventHandler<{ annotations: any[], senderId: string }>): UnsubscribeFn {
    const listener = (payload: { annotations: any[], senderId: string }) => {
      console.log('[SignalingService] Received annotation sync:', payload.annotations.length);
      handler(payload);
    };

    if (this.socket) {
      this.socket.on('annotation-sync', listener);
    } else {
      this.pendingListeners.push({ event: 'annotation-sync', listener });
    }

    return () => this.socket?.off('annotation-sync', listener);
  }

  /**
   * Sends annotation sync to peer
   */
  sendAnnotationSync(annotations: any[]): void {
    if (!this.socket || !this.roomId) {
      console.warn('[SignalingService] Cannot send annotations: not connected to room');
      return;
    }

    this.socket.emit('annotation-sync', {
      roomId: this.roomId,
      annotations,
    });

    console.log('[SignalingService] Sent annotation sync:', annotations.length);
  }

  /**
   * Sends a chat message to the room
   */
  sendChatMessage(message: {
    id: string;
    text: string;
    sender: string;
    timestamp: string;
    isHost: boolean;
  }): void {
    if (!this.socket || !this.roomId) {
      console.warn('[SignalingService] Cannot send chat message: not connected to room');
      return;
    }

    this.socket.emit('chat-message', {
      roomId: this.roomId,
      message: {
        ...message,
        senderId: this.socket.id,
      },
    });

    console.log('[SignalingService] Sent chat message:', message.text.substring(0, 50));
  }

  /**
   * Listens for chat messages
   */
  onChatMessage(handler: EventHandler<any>): UnsubscribeFn {
    const listener = (payload: any) => {
      const msg = payload?.message ?? payload;
      console.log('[SignalingService] Received chat message from:', msg?.senderId);
      handler(payload);
    };

    if (this.socket) {
      this.socket.on('chat-message', listener);
    } else {
      this.pendingListeners.push({ event: 'chat-message', listener });
    }

    return () => this.socket?.off('chat-message', listener);
  }

  // ========================================
  // Connection State
  // ========================================

  /**
   * Gets the current socket connection state
   */
  getConnectionState(): 'connected' | 'disconnected' | 'connecting' {
    if (!this.socket) {
      return 'disconnected';
    }

    if (this.socket.connected) {
      return 'connected';
    }

    // Socket.io doesn't have a 'connecting' property, so we check if it's not connected
    // If socket exists but is not connected, it's likely connecting
    return 'connecting';
  }

  /**
   * Checks if connected to a room
   */
  isInRoom(): boolean {
    return this.roomId !== null && this.socket?.connected === true;
  }

  /**
   * Gets the current room ID
   */
  getRoomId(): string | null {
    return this.roomId;
  }

  /**
   * Gets whether user is host
   */
  getIsHost(): boolean {
    return this.isHost;
  }

  // ========================================
  // Cleanup
  // ========================================

  /**
   * Cleans up socket connection and listeners
   */
  async cleanup(): Promise<void> {
    if (this.roomId) {
      await this.leaveRoom();
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.stateMachine = null;
    this.roomId = null;

    console.log('[SignalingService] Cleaned up');
  }
}
